import React, { useEffect, useState } from "react";
import {
    Box,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TableSortLabel,
    Button,
    Paper,
    Snackbar,
    Alert,
    Tooltip,
    Container,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import API_ENDPOINTS from "../../apis/endpoints";
import "./Brands.css";
import useSWR from "swr";
import SearchInput from "../../components/SearchInput";

const Brands = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("brand");
    const [sortDirection, setSortDirection] = useState("asc");
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const navigate = useNavigate();

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const queryParam = searchQuery ? `&query=${searchQuery}` : "";
    const sortParam = `&sort=${
        sortDirection === "desc" ? `-${sortColumn}` : sortColumn
    }`;
    const { data, isLoading, error } = useSWR(
        `${API_ENDPOINTS.BRANDS}?limit=${rowsPerPage}&offset=${
            page * rowsPerPage
        }${queryParam}${sortParam}`
    );

    const brands = data?.results ?? [];
    const totalBrands = data?.meta?.total ?? 0;
    const summary = data?.summary ?? { revenue: 0, deals: 0 };

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setPage(0);
        Sentry.addBreadcrumb({
            category: "user",
            message: `User searched for: ${searchTerm}`,
            level: "info",
        });
    };

    const handleSortRequest = (column) => {
        const isAsc = sortColumn === column && sortDirection === "asc";
        setSortDirection(isAsc ? "desc" : "asc");
        setSortColumn(column);
        Sentry.addBreadcrumb({
            category: "user",
            message: `Sorting by ${column} in ${isAsc ? "desc" : "asc"} order`,
            level: "info",
        });
    };

    const handleBrandClick = (brandId) => {
        navigate(`/dashboard/brand-details/${brandId}`);
        Sentry.addBreadcrumb({
            category: "navigation",
            message: `Navigating to brand ID: ${brandId}`,
            level: "info",
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatRevenue = (revenue) => {
        if (revenue >= 10000000) {
            return `₹${(revenue / 10000000).toFixed(2)}cr`;
        } else if (revenue >= 100000) {
            return `₹${(revenue / 100000).toFixed(2)}L`;
        } else {
            return `₹${revenue.toLocaleString()}`;
        }
    };

    const renderTalents = (talents) => {
        if (talents.length === 0) return "-";
        const displayedTalents = talents
            .slice(0, 3)
            .map((t) => t.name)
            .join(", ");
        if (talents.length > 3) {
            return (
                <Tooltip title={talents.map((t) => t.name).join(", ")} arrow>
                    <span>{displayedTalents}...</span>
                </Tooltip>
            );
        }
        return displayedTalents;
    };
    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    return (
        <Container maxWidth="lg">
            <Box className="brands-container">
                <Typography variant="featureEmphasis" gutterBottom>
                    Brands
                </Typography>

                <Box
                    sx={{
                        mt: 3,
                        mb: 3,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <SearchInput
                        onSearch={handleSearch}
                        placeHolder={"Brands..."}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        sx={{ width: "80vw" }}
                    />
                </Box>

                {isLoading ? (
                    <Box className="loader-container">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper className="table-paper">
                        <TableContainer className="table-container">
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="table-header-cell">
                                            <TableSortLabel
                                                active={sortColumn === "brand"}
                                                direction={
                                                    sortColumn === "brand"
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest("brand")
                                                }
                                            >
                                                Brand
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-header-cell">
                                            <TableSortLabel
                                                active={
                                                    sortColumn ===
                                                    "brand_category"
                                                }
                                                direction={
                                                    sortColumn ===
                                                    "brand_category"
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest(
                                                        "brand_category"
                                                    )
                                                }
                                            >
                                                Category
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-header-cell">
                                            <TableSortLabel
                                                active={
                                                    sortColumn === "sub_brand"
                                                }
                                                direction={
                                                    sortColumn === "sub_brand"
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest(
                                                        "sub_brand"
                                                    )
                                                }
                                            >
                                                Sub Brand
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-header-cell">
                                            <TableSortLabel
                                                active={
                                                    sortColumn ===
                                                    "sub_brand_category"
                                                }
                                                direction={
                                                    sortColumn ===
                                                    "sub_brand_category"
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest(
                                                        "sub_brand_category"
                                                    )
                                                }
                                            >
                                                Sub Brand Category
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-header-cell">
                                            <TableSortLabel
                                                active={
                                                    sortColumn === "revenue"
                                                }
                                                direction={
                                                    sortColumn === "revenue"
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest("revenue")
                                                }
                                            >
                                                Revenue (
                                                {formatRevenue(summary.revenue)}
                                                )
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-header-cell">
                                            <TableSortLabel
                                                active={sortColumn === "deals"}
                                                direction={
                                                    sortColumn === "deals"
                                                        ? sortDirection
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleSortRequest("deals")
                                                }
                                            >
                                                Deals ({summary.deals})
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className="table-header-cell">
                                            Talents
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {brands.length > 0 ? (
                                        brands.map((brand) => (
                                            <TableRow
                                                key={brand.id}
                                                className="table-row"
                                            >
                                                <TableCell className="table-cell">
                                                    {brand.brand}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    {brand.brand_category}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    {brand.sub_brand}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    {brand.sub_brand_category ||
                                                        "-"}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    {formatRevenue(
                                                        brand.revenue
                                                    )}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    {brand.deals}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    {renderTalents(
                                                        brand.talents
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                            >
                                                No brands found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={totalBrands}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                )}

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="error"
                        sx={{ width: "100%" }}
                    >
                        {error?.message
                            ? error?.message
                            : "An error occurred. Please try again later."}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
};

// Error boundary for monitoring component errors
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an error:", error, errorInfo);
        Sentry.captureException(error);
    }

    render() {
        if (this.state.hasError) {
            return <h1>Something went wrong.</h1>;
        }

        return this.props.children;
    }
}

export default function MonitoredBrands() {
    return (
        <ErrorBoundary>
            <Brands />
        </ErrorBoundary>
    );
}
